import { ApiKeyStamper } from "@turnkey/api-key-stamper";
import { AddWalletAccountsRequest, AddWalletAccountsResponse, CreateWalletRequest, CreateWalletResponse, ListWalletAccountsRequest, ListWalletAccountsResponse, ListWalletsRequest, ListWalletsResponse, SignRawPayloadRequest, SignRawPayloadResponse, SignRawPayloadsRequest, SignRawPayloadsResponse, SignTransactionRequest, SignTransactionResponse, TurnkeyConfig } from "./types";
import axios from "axios";

export class TurnkeySDK {
    protected stamper: ApiKeyStamper;

    constructor(private readonly config: TurnkeyConfig) {
        this.stamper = new ApiKeyStamper({
            apiPublicKey: this.config.apiPublicKey,
            apiPrivateKey: this.config.apiPrivateKey,
        });
    }

    async listWallets(request: ListWalletsRequest): Promise<ListWalletsResponse> {
        const requestBody = request

        const stamp = await this.stamper.stamp(JSON.stringify(requestBody));

        const response = await axios.post(
            `${this.config.baseUrl}/public/v1/query/list_wallets`,
            requestBody,
            { headers: { [stamp.stampHeaderName]: stamp.stampHeaderValue } }
        );

        return response.data;
    }

    async listWalletAccounts(request: ListWalletAccountsRequest): Promise<ListWalletAccountsResponse> {
        const requestBody = request

        const stamp = await this.stamper.stamp(JSON.stringify(requestBody));

        const response = await axios.post(
            `${this.config.baseUrl}/public/v1/query/list_wallet_accounts`,
            requestBody,
            { headers: { [stamp.stampHeaderName]: stamp.stampHeaderValue } }
        );

        return response.data;
    }

    async createWallet(request: CreateWalletRequest): Promise<CreateWalletResponse> {
        const requestBody = {
            type: 'ACTIVITY_TYPE_CREATE_SUB_ORGANIZATION_V7',
            timestampMs: new Date().getTime().toString(),
            organizationId: this.config.organizationId,
            parameters: {
                subOrganizationName: request.subOrganizationName,
                rootUsers: request.rootUsers.map((rootUser) => ({
                    userName: rootUser.userName,
                    apiKeys: rootUser.apiKeys,
                    authenticators: rootUser.authenticators,
                    oauthProviders: rootUser.oauthProviders,
                })),
                rootQuorumThreshold: request.rootQuorumThreshold,
                wallet: {
                    walletName: request.wallet.walletName,
                    accounts: request.wallet.accounts.map((account) => ({
                        pathFormat: account.pathFormat,
                        path: account.path,
                        curve: account.curve,
                        addressFormat: account.addressFormat,
                    })),
                    mnemonicLength: request.wallet.mnemonicLength,
                },
            },
        };

        const stamp = await this.stamper.stamp(JSON.stringify(requestBody));

		try {
			const response = await axios.post(
				`${this.config.baseUrl}/public/v1/submit/create_sub_organization`,
				requestBody,
				{ headers: { [stamp.stampHeaderName]: stamp.stampHeaderValue } }
			);
	
			return response.data;
		} catch (error: any) {
			console.log(error.response.data)
			throw new Error("Failed to create wallet");
		}
    }

    async addWalletAccount(request: AddWalletAccountsRequest): Promise<AddWalletAccountsResponse> {
        try {
			console.log(JSON.stringify(request, null, 2))
			const stamp = await this.stamper.stamp(JSON.stringify(request));
	
			const response = await axios.post(
				`${this.config.baseUrl}/public/v1/submit/create_wallet_accounts`,
				request,
				{ headers: { [stamp.stampHeaderName]: stamp.stampHeaderValue } }
			);
	
			return response.data;
		} catch (error: any) {
			console.log(error.response.data)
			if (axios.isAxiosError(error)) {
				throw new Error(`Axios error: ${error.response?.status} ${error.response?.statusText} - ${JSON.stringify(error.response?.data)}`);
			}
			throw new Error("Failed to add wallet account");
		}
    }

    async signRawPayload(request: SignRawPayloadRequest): Promise<SignRawPayloadResponse> {
        const stamp = await this.stamper.stamp(JSON.stringify(request));

        const response = await axios.post(
            `${this.config.baseUrl}/public/v1/submit/sign_raw_payload`,
            request,
            { headers: { [stamp.stampHeaderName]: stamp.stampHeaderValue } }
        );

        return response.data;
    }

    async signRawPayloads(request: SignRawPayloadsRequest): Promise<SignRawPayloadsResponse> {
        try {    
            const stamp = await this.stamper.stamp(JSON.stringify(request));
    
            const response = await axios.post(
                `${this.config.baseUrl}/public/v1/submit/sign_raw_payloads`,
                request,
                { headers: { [stamp.stampHeaderName]: stamp.stampHeaderValue } }
            );
    
            return response.data;
        } catch (error: any) {
            if (axios.isAxiosError(error)) {
                throw new Error(`Axios error: ${error.response?.status} ${error.response?.statusText} - ${JSON.stringify(error.response?.data)}`);
            }
            throw new Error("Failed to sign raw payloads");
        }
    }

    async signTransaction(request: SignTransactionRequest): Promise<SignTransactionResponse> {
        const stamp = await this.stamper.stamp(JSON.stringify(request));

        const response = await axios.post(
            `${this.config.baseUrl}/public/v1/submit/sign_transaction`,
            request,
            { headers: { [stamp.stampHeaderName]: stamp.stampHeaderValue } }
        );

        return response.data;
    }
}

export * from "./types";
export { TurnkeyAuthenticator } from "./authenticator";
export type { TurnkeyConfig } from "./types";